import { useContext, useState } from 'react';
import { Box, CircularProgress, Dialog, DialogTitle } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import useGroupsService from '../../../services/GroupsService';
import DiscountList from '../../../components/discounts/DiscountsList';
import SttError from '../../../components/error/SttError';
import { AppContext } from '../../../sporttia/AppContext';
import { getErrorMessage } from '../../../lib/utils';
import { GetGroupDiscounts200 } from '../../../types/api/paths/Group';
import useScsService from '../../../services/ScsService';

type GroupDiscountsListProps = {
    groupId: number;
};

export default function GroupDiscountsList({
    groupId,
}: GroupDiscountsListProps) {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();

    const [openDialog, setOpenDialog] = useState(false);

    const queryClient = useQueryClient();

    const groupsService = useGroupsService();
    const getGroupDiscountsQuery = groupsService.useGetGroupDiscounts(groupId, {
        rows: 1000,
    });

    const scsService = useScsService();
    const getScDiscountsQuery = scsService.useGetScsDiscounts(
        cxt.sc!.id,
        { rows: 1000 },
        { enabled: openDialog } as any,
    );

    const createGroupDiscountMutation = groupsService.useCreateGroupDiscount({
        onSuccess: (result: any) => {
            queryClient.setQueryData<GetGroupDiscounts200 | undefined>(
                getGroupDiscountsQuery.queryKey,
                (old: any) => {
                    if (!old) {
                        return undefined;
                    }

                    return {
                        ...old,
                        count: old.count + 1,
                        rows: [...old.rows, result.discount],
                    };
                },
            );

            cxt.showMessage('S', t('group.discountAdded'));
            setOpenDialog(false);
        },
        onError: (error: any) => {
            cxt.showMessage('E', getErrorMessage(error));
        },
    });

    const deleteGroupDiscountMutation = groupsService.useDeleteGroupDiscount({
        onSuccess: (_result: any, variables: any) => {
            queryClient.setQueryData<GetGroupDiscounts200 | undefined>(
                getGroupDiscountsQuery.queryKey,
                (old: any) => {
                    if (!old) {
                        return undefined;
                    }

                    return {
                        ...old,
                        count: old.count - 1,
                        rows: old?.rows.filter(
                            (row: any) => row.id !== variables.discountId,
                        ),
                    };
                },
            );

            cxt.showMessage('S', t('group.discountDeleted'));
            setOpenDialog(false);
        },
        onError: (error: any) => {
            cxt.showMessage('E', getErrorMessage(error));
        },
    });

    if (
        getGroupDiscountsQuery.isLoading ||
        createGroupDiscountMutation.isPending ||
        deleteGroupDiscountMutation.isPending
    ) {
        return (
            <Box display="flex" justifyContent="center" paddingTop={12}>
                <CircularProgress />
            </Box>
        );
    }

    if (getGroupDiscountsQuery.isError || getScDiscountsQuery.isError) {
        const error = getGroupDiscountsQuery.error || getScDiscountsQuery.error;
        return <SttError message={getErrorMessage(error)} />;
    }

    if (!getGroupDiscountsQuery.isSuccess) {
        return null;
    }

    return (
        <>
            <DiscountList
                discounts={getGroupDiscountsQuery.data.rows}
                onAdd={() => setOpenDialog(true)}
                onDelete={(discount) => {
                    deleteGroupDiscountMutation.mutate({
                        discountId: discount.id,
                        groupId,
                    });
                }}
            />

            <Dialog
                onClose={() => setOpenDialog(false)}
                open={openDialog}
                fullWidth
                maxWidth="xs"
            >
                {getScDiscountsQuery.isLoading && (
                    <Box display="flex" justifyContent="center" paddingY={12}>
                        <CircularProgress />
                    </Box>
                )}

                {getScDiscountsQuery.isSuccess && (
                    <>
                        <DialogTitle>{t('generic.discounts')}</DialogTitle>
                        <DiscountList
                            // Filtrar descuentos ya añadidos al grupo
                            discounts={getScDiscountsQuery.data.rows.filter(
                                (scDiscount) =>
                                    !getGroupDiscountsQuery.data.rows.some(
                                        (groupDiscount) =>
                                            groupDiscount.id === scDiscount.id,
                                    ),
                            )}
                            onSelect={(discount) => {
                                createGroupDiscountMutation.mutate({
                                    discountId: discount.id,
                                    groupId,
                                });
                            }}
                            showTitle={false}
                        />
                    </>
                )}
            </Dialog>
        </>
    );
}
